import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowLeft,
  Send,
  FileDown,
  User,
  Building2,
  UserCircle,
  Plus,
  X,
  Mail,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Copy,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { downloadClaimPdf, triggerBlobDownload, openPdfInNewTab } from "@/lib/pdfDownload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SendReport = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [pdfReady, setPdfReady] = useState(null);
  const [sent, setSent] = useState(false);

  // Recipients
  const [sendToClient, setSendToClient] = useState(true);
  const [sendToInsurance, setSendToInsurance] = useState(false);
  const [sendToSelf, setSendToSelf] = useState(true);
  const [otherEmails, setOtherEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  
  // Insurance email
  const [insuranceEmail, setInsuranceEmail] = useState("");

  // Custom message
  const [customMessage, setCustomMessage] = useState("");

  useEffect(() => {
    fetchClaim();
  }, [id]);

  const fetchClaim = async () => {
    try {
      const response = await axios.get(`${API}/claims/${id}`);
      setClaim(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement du dossier");
      navigate("/claims");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    setPdfReady(null);
    try {
      const { blobUrl, filename } = await downloadClaimPdf(id, claim?.file_number, token);
      triggerBlobDownload(blobUrl, filename);
      setPdfReady({ blobUrl, filename });
      toast.success("PDF téléchargé", {
        description: "Si le fichier ne s'ouvre pas, utilisez les options ci-dessous.",
      });
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    } finally {
      setDownloading(false);
    }
  };

  const handleOpenPdf = () => {
    if (pdfReady?.blobUrl) openPdfInNewTab(pdfReady.blobUrl);
  };

  const handleCopyPdfLink = () => {
    if (pdfReady?.blobUrl) {
      navigator.clipboard.writeText(pdfReady.blobUrl).then(() => toast.success("Lien copié")).catch(() => toast.error("Impossible de copier"));
    }
  };

  const addOtherEmail = () => {
    if (newEmail && newEmail.includes("@")) {
      setOtherEmails([...otherEmails, newEmail]);
      setNewEmail("");
    } else {
      toast.error("Veuillez entrer un email valide");
    }
  };

  const removeOtherEmail = (index) => {
    setOtherEmails(otherEmails.filter((_, i) => i !== index));
  };

  const handleSendEmail = async () => {
    // Build recipients list
    const recipients = [];
    
    if (sendToClient && claim.client_email) {
      recipients.push({ type: "client" });
    }
    
    if (sendToInsurance && insuranceEmail) {
      recipients.push({ type: "insurance", email: insuranceEmail, name: claim.client_insurance });
    }
    
    if (sendToSelf) {
      recipients.push({ type: "self" });
    }
    
    otherEmails.forEach(email => {
      recipients.push({ type: "other", email });
    });
    
    if (recipients.length === 0) {
      toast.error("Veuillez sélectionner au moins un destinataire");
      return;
    }
    
    setSending(true);
    try {
      const response = await axios.post(`${API}/claims/${id}/send-email`, {
        recipients,
        message: customMessage || null,
      });
      
      toast.success(response.data.message);
      setSent(true);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Erreur lors de l'envoi";
      toast.error(errorMsg);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  if (!claim) {
    return null;
  }

  const hasClientEmail = !!claim.client_email;
  const clientName = `${claim.client_first_name || ""} ${claim.client_name || ""}`.trim() || "Client";

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="send-report">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/claims/${id}`)}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-900">
                Envoyer le rapport
              </h1>
              <p className="text-sm text-slate-500">{claim.file_number}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* Success message */}
        {sent && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <div>
                  <p className="font-medium text-green-800">Email envoyé avec succès !</p>
                  <p className="text-sm text-green-600">Le rapport a été envoyé aux destinataires sélectionnés.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Résumé du dossier</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Client</p>
                <p className="font-medium">{clientName}</p>
              </div>
              <div>
                <p className="text-slate-500">Adresse</p>
                <p className="font-medium">{claim.address}</p>
              </div>
              <div>
                <p className="text-slate-500">Assurance</p>
                <p className="font-medium">{claim.client_insurance || "Non renseignée"}</p>
              </div>
              <div>
                <p className="text-slate-500">Photos</p>
                <p className="font-medium">{claim.photos?.length || 0} photo(s)</p>
              </div>
            </div>
            
            <Button
              variant="outline"
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="w-full h-12 mt-4"
              data-testid="download-preview-btn"
            >
              {downloading ? (
                <div className="spinner w-5 h-5 border-sky-600 border-t-transparent" />
              ) : (
                <>
                  <FileDown className="w-5 h-5 mr-2" />
                  Télécharger l'aperçu PDF
                </>
              )}
            </Button>

            {pdfReady && (
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-2 mt-3" data-testid="pdf-fallback-send">
                <p className="text-xs text-slate-500">
                  Si le téléchargement ne démarre pas :
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleOpenPdf} className="flex-1 text-xs" data-testid="open-pdf-send-btn">
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                    Ouvrir le PDF
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleCopyPdfLink} className="flex-1 text-xs" data-testid="copy-pdf-send-btn">
                    <Copy className="w-3.5 h-3.5 mr-1.5" />
                    Copier le lien
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recipients Selection */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Mail className="w-5 h-5 text-sky-600" />
              Destinataires
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Client */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-slate-600" />
                <div>
                  <p className="font-medium">{clientName}</p>
                  <p className="text-sm text-slate-500">
                    {hasClientEmail ? claim.client_email : "Aucun email renseigné"}
                  </p>
                </div>
              </div>
              <Checkbox
                checked={sendToClient}
                onCheckedChange={setSendToClient}
                disabled={!hasClientEmail}
                data-testid="checkbox-client"
              />
            </div>

            {/* Insurance */}
            <div className="p-4 bg-slate-50 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-slate-600" />
                  <div>
                    <p className="font-medium">{claim.client_insurance || "Assurance"}</p>
                    <p className="text-sm text-slate-500">Email de l'assurance</p>
                  </div>
                </div>
                <Checkbox
                  checked={sendToInsurance}
                  onCheckedChange={setSendToInsurance}
                  data-testid="checkbox-insurance"
                />
              </div>
              {sendToInsurance && (
                <Input
                  type="email"
                  placeholder="Email de l'assurance"
                  value={insuranceEmail}
                  onChange={(e) => setInsuranceEmail(e.target.value)}
                  className="h-11"
                  data-testid="input-insurance-email"
                />
              )}
            </div>

            {/* Self */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <UserCircle className="w-5 h-5 text-slate-600" />
                <div>
                  <p className="font-medium">Moi-même</p>
                  <p className="text-sm text-slate-500">Recevoir une copie</p>
                </div>
              </div>
              <Checkbox
                checked={sendToSelf}
                onCheckedChange={setSendToSelf}
                data-testid="checkbox-self"
              />
            </div>

            {/* Other emails */}
            <div className="p-4 bg-slate-50 rounded-lg space-y-3">
              <div className="flex items-center gap-2">
                <Input
                  type="email"
                  placeholder="Ajouter un autre destinataire"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && addOtherEmail()}
                  className="h-11 flex-1"
                  data-testid="input-other-email"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={addOtherEmail}
                  className="h-11"
                  data-testid="add-other-email-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {otherEmails.length > 0 && (
                <div className="space-y-2">
                  {otherEmails.map((email, index) => (
                    <div key={index} className="flex items-center justify-between bg-white p-2 rounded border">
                      <span className="text-sm">{email}</span>
                      <button
                        type="button"
                        onClick={() => removeOtherEmail(index)}
                        className="text-red-500 hover:text-red-700"
                        data-testid={`remove-email-${index}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Custom Message */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Message personnalisé (optionnel)</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Ajoutez un message personnalisé qui sera inclus dans l'email..."
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              className="min-h-[100px]"
              data-testid="textarea-message"
            />
          </CardContent>
        </Card>

        {/* Warning if no valid recipients */}
        {!sendToClient && !sendToInsurance && !sendToSelf && otherEmails.length === 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <p className="text-sm text-amber-800">
                  Veuillez sélectionner au moins un destinataire
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Send Button */}
        <Button
          onClick={handleSendEmail}
          disabled={sending || (!sendToClient && !sendToInsurance && !sendToSelf && otherEmails.length === 0)}
          className="w-full h-14 bg-sky-600 hover:bg-sky-700 text-lg"
          data-testid="send-email-btn"
        >
          {sending ? (
            <div className="spinner w-6 h-6 border-white border-t-transparent" />
          ) : (
            <>
              <Send className="w-5 h-5 mr-2" />
              Envoyer le rapport par email
            </>
          )}
        </Button>

        {/* Back to claim detail */}
        <Button
          variant="outline"
          onClick={() => navigate(`/claims/${id}`)}
          className="w-full h-12"
          data-testid="back-to-claim-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour au dossier
        </Button>
      </main>
    </div>
  );
};

export default SendReport;
